/**
 * Nastavení: připomínka hlášení počtu včelstev do kalendáře telefonu.
 * Webová aplikace nemůže do kalendáře zapisovat sama – vytvoří událost (.ics),
 * kterou telefon nabídne přidat, nebo otevře Google Kalendář s předvyplněnou událostí.
 */
import { useEffect, useState } from 'react';
import { getSetting, setSetting } from '../db/db';
import { googleCalendarUrl, icsCalendar, type IcsEvent } from '../lib/ics';
import { todayISO } from '../db/rules';
import { Chips, DateInput, Field, Section, toast } from './ui';
import { Icon } from './icons';
import { saved } from './confirm';

const SETTING = 'reportReminder';
const PORTAL = 'https://www.portalvcelarstvi.cz/';

interface Reminder {
  date: string;
  time: string;
  alarmDays: number;
  yearly: boolean;
}

/** Uložené datum posunuté na nejbližší budoucí výročí (stejný den a měsíc). */
function upcoming(date: string | undefined, today = todayISO()) {
  if (!date) return nextSept1(today);
  let y = Number(today.slice(0, 4));
  let d = `${y}${date.slice(4)}`;
  if (date >= today) return date;
  if (d < today) d = `${++y}${date.slice(4)}`;
  return d;
}

/** Nejbližší 1. září (letos, nebo příští rok). */
function nextSept1(today = todayISO()) {
  const y = Number(today.slice(0, 4));
  return today <= `${y}-09-01` ? `${y}-09-01` : `${y + 1}-09-01`;
}

export function reminderEvent(r: Reminder): IcsEvent {
  return {
    uid: 'hlaseni-poctu-vcelstev@vcelarska-aplikace.pages.dev',
    date: r.date,
    time: r.time,
    durationMin: 30,
    title: 'Nahlásit počet včelstev',
    description:
      'Hlášení počtu včelstev a umístění stanovišť k 1. září, nejpozději do 15. září.\n' +
      'Přehled počtů v aplikaci: https://vcelarska-aplikace.pages.dev/#/hlaseni\n' +
      `Podat hlášení: ${PORTAL}`,
    url: 'https://vcelarska-aplikace.pages.dev/#/hlaseni',
    alarmDaysBefore: r.alarmDays,
    yearly: r.yearly,
  };
}

export function downloadIcs(text: string, name = 'hlaseni-vcelstev.ics') {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function ReportReminderSetting() {
  const [r, setR] = useState<Reminder | null>(null);
  useEffect(() => {
    getSetting<Reminder>(SETTING).then((s) => setR({ time: '09:00', alarmDays: 0, yearly: true, ...s, date: upcoming(s?.date) }));
  }, []);
  if (!r) return null;
  const set = (p: Partial<Reminder>) => setR({ ...r, ...p });
  const remember = () => setSetting(SETTING, r);

  return (
    <Section title="Připomínka hlášení včelstev">
      <p className="muted">Zvolte, kdy vám má kalendář v telefonu připomenout hlášení počtu včelstev (termín je k 1. 9., nejpozději do 15. 9.).</p>
      <p className="small link-row">
        <a href={PORTAL} target="_blank" rel="noopener noreferrer">
          Podat hlášení na Portálu včelaře (portalvcelarstvi.cz) ↗
        </a>
        {' · '}
        <a href="#/hlaseni">Přehled počtů v aplikaci</a>
      </p>
      <div className="row two">
        <Field label="Datum" htmlFor="rr-date">
          <DateInput id="rr-date" value={r.date} min={todayISO()} onChange={(v) => set({ date: v })} />
        </Field>
        <Field label="Čas" htmlFor="rr-time">
          <input id="rr-time" type="time" value={r.time} onChange={(e) => e.target.value && set({ time: e.target.value })} />
        </Field>
      </div>
      <Field label="Upozornit">
        <Chips
          ariaLabel="Upozornit"
          options={[
            [0, 'v ten den'],
            [1, 'den předem'],
            [3, '3 dny předem'],
            [7, 'týden předem'],
          ]}
          value={r.alarmDays}
          onChange={(v) => v != null && set({ alarmDays: v })}
        />
      </Field>
      <label className="check">
        <input type="checkbox" checked={r.yearly} onChange={(e) => set({ yearly: e.target.checked })} />
        Opakovat každý rok
      </label>
      <div className="quick-row">
        <button
          className="btn primary"
          onClick={async () => {
            await remember();
            downloadIcs(icsCalendar(reminderEvent(r)));
            saved('Připomínka připravena');
            setTimeout(() => toast('Otevřete stažený soubor a potvrďte Přidat do kalendáře'), 1400);
          }}
        >
          <Icon name="calendar" size={20} /> Přidat do kalendáře
        </button>
        <a className="btn" href={googleCalendarUrl(reminderEvent(r))} target="_blank" rel="noopener noreferrer" onClick={() => void remember()}>
          Google Kalendář
        </a>
      </div>
      <p className="small muted">
        „Přidat do kalendáře“ funguje v iPhonu, Androidu i Outlooku – telefon nabídne událost uložit. Když používáte Google Kalendář, je
        rychlejší druhé tlačítko. Při změně data přidejte událost znovu (stará se v kalendáři přepíše nebo ji smažte ručně).
      </p>
    </Section>
  );
}
